/* eslint-disable comma-dangle */
import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import {
  CheckOutlined,
  CloudServerOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  MoreOutlined,
  RedoOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  List,
  Input,
  Card,
  Avatar,
  Tabs,
  Badge,
  Dropdown,
  Menu,
  Modal,
  Typography,
  Button,
  Space,
  Tooltip,
} from 'antd';
import { debounce } from 'lodash';
import IframeResizer from 'iframe-resizer-react';
import * as axios from 'axios';
import { getAppList, getLoading } from '@store/admin/apps/selectors';
import { getUserProfile, pulse5ApiUsageDashboardFeatureSelector } from '@store/common/selectors';
import { list, update, del } from '@store/admin/apps/actions';
import PageHeader from '@components/common/PageHeader';
import { singleAppPrefix } from '../../../routers';

const { TabPane } = Tabs;
const { Search } = Input;
const { confirm } = Modal;
const { Paragraph } = Typography;

const APP_LIMIT = 50;

const ApplicationListItem = withRouter(
  connect(null, { deactivate: update, activate: update, remove: del })(
    ({ app, deactivate, activate, remove }) => {
      const confirmDelete = (appToBeDeleted) => {
        confirm({
          title: 'Do you want to delete this application?',
          content: appToBeDeleted.title,
          onOk() {
            remove(appToBeDeleted);
          },
        });
      };

      const onActivate = () => {
        activate({ ...app, inactive: false });
      };

      const onDeactivate = () => {
        deactivate({ ...app, inactive: true });
      };

      const onAppDelete = () => {
        confirmDelete(app);
      };

      return (
        <List.Item
          key={app.clientId}
          actions={[
            <Paragraph type="secondary" key="para-one">
              {`updated at ${new Date(
                app.updated_at,
              ).toLocaleDateString()} by ${app.updatedBy.email} `}
            </Paragraph>,
            <Paragraph type="secondary" key="para-two">
              {`created at ${new Date(
                app.created_at,
              ).toLocaleDateString()} by ${app.createdBy.email} `}
            </Paragraph>,
          ]}
          extra={
            <Dropdown
              key="1"
              overlay={
                <Menu>
                  <Menu.Item>
                    <Link to={`${singleAppPrefix}/manage/apps/detail/${app.clientId}`}>
                      <Space>
                        <EditOutlined />
                        <span>Edit</span>
                      </Space>
                    </Link>
                  </Menu.Item>
                  {app.inactive && (
                    <Menu.Item onClick={onActivate}>
                      <Space>
                        <CheckOutlined />
                        <span>Activate</span>
                      </Space>
                    </Menu.Item>
                  )}
                  {!app.inactive && (
                    <Menu.Item onClick={onDeactivate}>
                      <Space>
                        <StopOutlined />
                        <span>Deactivate</span>
                      </Space>
                    </Menu.Item>
                  )}
                  <Menu.Item onClick={onAppDelete}>
                    <Space>
                      <DeleteOutlined />
                      <span>Delete</span>
                    </Space>
                  </Menu.Item>
                </Menu>
              }
            >
              <Button>
                <MoreOutlined />
              </Button>
            </Dropdown>
          }
        >
          <List.Item.Meta
            avatar={<Avatar size="large" icon={<CloudServerOutlined />} />}
            title={
              <Link to={`${singleAppPrefix}/manage/apps/detail/${app.clientId}`}>{app.title}</Link>
            }
            description={app.description}
          />
          <Paragraph>{`Client ID: ${app.clientId}`}</Paragraph>
        </List.Item>
      );
    },
  ),
);

const ApplicationList = ({ loading, apps, refresh, userProfile }) => {
  const [usageURL, setUsageURL] = useState('');
  useEffect(() => {
    if (!loading && !apps) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // eslint-disable-next-line camelcase
    const { customer_id, profile_id, hidden } = userProfile;
    axios
      .get('https://api.conviva.com/looker-embedded-v1/dashboards/api-usage', {
        params: {
          customer: customer_id,
          user: profile_id,
          internal: hidden,
        },
      })
      .then((resp) => {
        setUsageURL(resp.data.link);
      });
  }, [userProfile]);
  const [filter, setFilter] = useState('');
  const onSearchChange = debounce(setFilter, 200);
  const isPulseAPIUsageEnabled = useSelector(pulse5ApiUsageDashboardFeatureSelector);

  if (loading || !apps) {
    return null;
  }
  const filteredApps = filter
    ? apps.filter(
        (app) =>
          app.title.toLowerCase().includes(filter) || app.clientId.toLowerCase().includes(filter),
      )
    : apps;
  const activeApps = filteredApps.filter((app) => !app.inactive);
  const inactiveApps = filteredApps.filter((app) => app.inactive);

  const onSearch = (e) => {
    onSearchChange(e.target.value.toLowerCase());
  };

  const renderApp = (app) => <ApplicationListItem app={app} />;

  const reachedLimit = filteredApps.length >= APP_LIMIT;

  /* eslint-disable */
  return (
    <div>
      <PageHeader
        title="Applications"
        extra={[
          reachedLimit ? (
            <Tooltip
              key="limit-tooltip"
              title={`You have reached the limit of ${APP_LIMIT} tokens.`}
            >
              <Button disabled={true}>Create New App</Button>
            </Tooltip>
          ) : (
            <Link key="1" to={`${singleAppPrefix}/manage/apps/create`}>
              <Button>Create New App</Button>
            </Link>
          ),
          <Dropdown
            key="2"
            overlay={
              <Menu>
                <Menu.Item onClick={refresh}>
                  <Space>
                    <RedoOutlined />
                    <span>Refresh</span>
                  </Space>
                </Menu.Item>
              </Menu>
            }
          >
            <Button>
              <span>More</span>
              <DownOutlined />
            </Button>
          </Dropdown>,
        ]}
        style={{
          marginTop: window.__POWERED_BY_QIANKUN__ ? '0' : '5px',
          marginLeft: '1px',
          backgroundColor: 'white',
        }} // eslint-disable-line
      />
      <Card>
        <Search placeholder="Search by Name or Client ID" onChange={onSearch} />
        <Tabs defaultActiveKey="active" animated={false}>
          <TabPane
            tab={
              <Badge
                count={activeApps.length}
                style={{
                  backgroundColor: '#fff',
                  color: '#999',
                  boxShadow: '0 0 0 1px #d9d9d9 inset',
                }}
              >
                Active&nbsp;&nbsp;&nbsp;
              </Badge>
            }
            key="active"
          >
            <List itemLayout="vertical" dataSource={activeApps} renderItem={renderApp} />
          </TabPane>
          <TabPane
            tab={
              <Badge
                count={inactiveApps.length}
                style={{
                  backgroundColor: '#fff',
                  color: '#999',
                  boxShadow: '0 0 0 1px #d9d9d9 inset',
                }}
              >
                Inactive&nbsp;&nbsp;&nbsp;
              </Badge>
            }
            key="inactive"
          >
            <List itemLayout="vertical" dataSource={inactiveApps} renderItem={renderApp} />
          </TabPane>
          {usageURL ? (
            <TabPane tab="API Usage" key="API Usage">
              {isPulseAPIUsageEnabled ? (
                <IframeResizer
                  id="looker-iframe"
                  style={{
                    width: '1px',
                    minWidth: '100%',
                    border: 'none',
                    minHeight: '100vh',
                  }}
                  src={usageURL}
                />
              ) : (
                'We are upgrading the API Usage dashboard. Please contact your Conviva representative for more details.'
              )}
            </TabPane>
          ) : null}
        </Tabs>
      </Card>
    </div>
  );
};
/* eslint-enable */

ApplicationList.defaultProps = {
  apps: null,
  userProfile: {},
};

const mapStateToProps = (state) => ({
  apps: getAppList(state),
  loading: getLoading(state),
  userProfile: getUserProfile(state),
});

export default connect(mapStateToProps, { refresh: list })(ApplicationList);
